import React from 'react';
import ReactDOM from 'react-dom/client';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import App from './App';
import { performanceMonitor } from './utils/bundleAnalyzer';
import { initializeAnalytics } from './utils/analytics';
import { initializeClarity } from './utils/clarity';

// One-time recovery for transient chunk load errors during development/HMR
(() => {
  if (typeof window === 'undefined') return;
  const RETRY_FLAG = '__kt_chunk_retry';
  const reloadOnce = () => {
    if (sessionStorage.getItem(RETRY_FLAG) === '1') return;
    try {
      sessionStorage.setItem(RETRY_FLAG, '1');
      const url = new URL(window.location.href);
      url.searchParams.set('v', String(Date.now()));
      window.location.replace(url.toString());
    } catch (_e) {
      window.location.reload();
    }
  };

  const isChunkError = (msg = '') => /ChunkLoadError|Loading chunk|Unexpected token </i.test(String(msg));

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event && event.reason ? (event.reason.message || String(event.reason)) : '';
    if (isChunkError(reason)) reloadOnce();
  });

  window.addEventListener(
    'error',
    (event) => {
      // Detect failed script loads for split chunks
      const target = event && event.target;
      if (target && target.tagName === 'SCRIPT') {
        const src = target.src || '';
        if (/\/static\/js\/.*chunk\.js(\?.*)?$/i.test(src)) reloadOnce();
      }
    },
    true
  );
})();

// Create a custom theme for Kranawetter Transport
const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2', // Professional blue
      light: '#42a5f5',
      dark: '#1565c0',
    },
    secondary: {
      main: '#ff9800', // Orange accent for CTAs
      light: '#ffb74d',
      dark: '#f57c00',
    },
    background: {
      default: '#fafafa',
      paper: '#ffffff',
    },
    text: {
      primary: '#212121', // Dark gray for excellent contrast
      secondary: '#424242', // Darker gray for better contrast (was #757575)
      disabled: '#757575', // Standard disabled text
    },
    // Custom palette additions for accessibility
    grey: {
      50: '#fafafa',
      100: '#f5f5f5',
      200: '#eeeeee',
      300: '#e0e0e0',
      400: '#bdbdbd',
      500: '#9e9e9e',
      600: '#757575',
      700: '#616161',
      800: '#424242',
      900: '#212121',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontFamily: '"Oswald", sans-serif',
      textTransform: 'uppercase',
      letterSpacing: '0.06em',
      fontWeight: 700,
      fontSize: '3.4rem',
      lineHeight: 1.1,
    },
    h2: {
      fontFamily: '"Oswald", sans-serif',
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      fontWeight: 700,
      fontSize: '2.6rem',
      lineHeight: 1.15,
    },
    h3: {
      fontFamily: '"Oswald", sans-serif',
      textTransform: 'uppercase',
      letterSpacing: '0.04em',
      fontWeight: 600,
      fontSize: '2rem',
      lineHeight: 1.2,
    },
    h4: {
      fontWeight: 500,
      fontSize: '1.75rem',
      lineHeight: 1.4,
    },
    h5: {
      fontWeight: 500,
      fontSize: '1.5rem',
      lineHeight: 1.5,
    },
    body1: {
      fontSize: '1.1rem',
      lineHeight: 1.55,
      fontWeight: 500,
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 600,
          padding: '12px 24px',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        },
      },
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <App />
    </ThemeProvider>
  </React.StrictMode>
);

// Initialize analytics and behavior tracking
initializeAnalytics();
initializeClarity();

// Initialize performance monitoring for production builds
if (process.env.NODE_ENV === 'production') {
  // Start monitoring Core Web Vitals
  performanceMonitor.startMonitoring((report) => {
    // In production, you could send this to your analytics service
    console.log('Performance Report:', report);
    
    // Example: Send to Google Analytics or other service
    // gtag('event', 'performance_report', {
    //   performance_score: report.score,
    //   fcp: report.coreWebVitals.fcp,
    //   lcp: report.coreWebVitals.lcp,
    //   bundle_size: report.bundlePerformance.jsResources.totalSize
    // });
  }, 60000); // Monitor every minute
} 